import React from "react";
import UIPreview from "./UIPreview";

const normalizeInput = (value) => {
  if (!value) return "";

  // Trim extra spaces
  const trimmed = value.trim();

  // If already multiline, keep it
  if (trimmed.includes("\n")) {
    return trimmed;
  }

  // Convert space-separated input → newline-separated
  return trimmed
    .split(/\s+/)
    .join("\n");
};

const InputOutput = ({
  input,
  setInput,
  output,
  isDarkMode,
  activeTab,
  setActiveTab,
  disabled = false,
  language = 'java',
  previewFiles = {},
  isPreviewLoading = false,
}) => {
  // Determine which tabs to show based on language
  const isUIMode = language === 'react' || language === 'angular';
  const availableTabs = isUIMode ? ['preview'] : ['input', 'output'];
  return (
    <div
      className={`flex flex-col h-full rounded-lg shadow-inner ${
        isDarkMode ? "bg-gray-800 text-white" : "bg-[#F7F4FD] text-[#191F2D] border border-[#EAE4F7]"
      }`}
    >
      {/* Tabs as Section Header */}
      <div className="flex justify-between items-center mb-2 px-3 pt-3">
        <div className="flex space-x-2 text-sm font-semibold">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => !disabled && setActiveTab(tab)}
              className={`px-3 py-1 rounded-md transition-colors ${
                activeTab === tab
                  ? "bg-[#4B1498] text-white"
                  : isDarkMode
                  ? "text-gray-400 hover:bg-gray-700"
                  : "bg-[#EEE9FA] text-[#7A5BC7] hover:bg-[#E5DCF8]"
              }`}
            >
              {tab === "preview"
                ? "🎨 Preview"
                : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-col flex-grow px-3 pb-3 overflow-hidden">
        {isUIMode && activeTab === "preview" ? (
          <UIPreview
            language={language}
            files={previewFiles}
            isDarkMode={isDarkMode}
            isLoading={isPreviewLoading}
          />
        ) : activeTab === "input" ? (
          <textarea
            value={input}
            onChange={(e) => {
              if (!disabled) {
                setInput(e.target.value);
              }
            }}
            placeholder="Enter input here"
            disabled={disabled}
            className={`flex-grow w-full p-2 rounded-lg border text-sm font-mono resize-y min-h-[100px] ${
              isDarkMode
                ? "bg-gray-900 text-white border-gray-700"
                : "bg-white text-[#191F2D] border-[#ECE8F4] placeholder:text-[#9699AA]"
            }`}
          />
        ) : (
          <div className="flex-grow overflow-y-auto max-h-[calc(100vh-200px)] p-2 rounded-lg text-sm font-mono whitespace-pre-wrap space-y-4">
            {output && typeof output === "object" ? (
              <div className="space-y-4">
                {/* If there's an error, show ONLY the error at the top */}
                {output.stderr ? (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg shadow-sm">
                    <div className="flex items-center mb-2">
                      <span className="text-red-600 dark:text-red-400 mr-2">
                        ⚠️
                      </span>
                      <strong className="text-red-800 dark:text-red-200">
                        Error
                      </strong>
                    </div>
                    <pre className="text-sm font-mono bg-white dark:bg-gray-800 p-3 rounded border whitespace-pre-wrap overflow-x-auto text-red-700 dark:text-red-300">
                      {output.stderr}
                    </pre>
                  </div>
                ) : (
                  <>
                    {/* Show output only if there's no error */}
                    <div className="p-4 bg-[#F0EBFF] dark:bg-[#2C2140] border-l-4 border-[#A66FFF] rounded-lg shadow-sm">
                      <div className="flex items-center mb-2">
                        <span className="text-[#4B1498] dark:text-[#C9A7FF] mr-2">
                          📤
                        </span>
                        <strong className="text-[#191F2D] dark:text-white">
                          Output
                        </strong>
                      </div>
                      <pre className="text-sm font-mono bg-white dark:bg-gray-800 p-3 rounded border whitespace-pre-wrap overflow-x-auto">
                        {output.stdout ? output.stdout : "No output generated"}
                      </pre>
                    </div>

                    {/* Show test results only if there's no error */}
                    {output.test_results && output.test_results.length > 0 && (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 rounded-lg shadow-sm">
                        <div className="flex items-center mb-2">
                          <span className="text-yellow-600 dark:text-yellow-400 mr-2">
                            🧪
                          </span>
                          <strong className="text-yellow-800 dark:text-yellow-200">
                            Test Results
                          </strong>
                        </div>
                        <div className="space-y-2">
                          {output.test_results.map((test, idx) => (
                            <div
                              key={idx}
                              className="p-3 bg-white dark:bg-gray-800 rounded border shadow-sm"
                            >
                              <p>
                                <strong>Test {test.test_case}:</strong>{" "}
                                {test.passed === true
                                  ? "✅ Passed"
                                  : test.passed === false
                                  ? "❌ Failed"
                                  : "N/A"}
                              </p>
                              <p>
                                <strong>Input:</strong>{" "}
                                {JSON.stringify(test.input) || "N/A"}
                              </p>
                              <p>
                                <strong>Expected:</strong>{" "}
                                {test.expected_output || "N/A"}
                              </p>
                              <p>
                                <strong>Actual:</strong>{" "}
                                {test.actual_output || "N/A"}
                              </p>
                              <p>
                                <strong>Time:</strong> {test.time || "N/A"}
                              </p>
                              {test.error && (
                                <p>
                                  <strong>Error:</strong> {test.error}
                                </p>
                              )}
                            </div>
                          ))}
                          <p className="text-center font-semibold text-yellow-700 dark:text-yellow-300">
                            Summary: {output.summary?.passed || 0}/
                            {output.summary?.total || 0} passed
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Status and Time (shown for both success and error cases) */}
                <div className="flex items-center space-x-4 p-3 bg-[#F6F3FC] dark:bg-gray-700 rounded-lg shadow-sm">
                  <div className="flex items-center">
                    <span
                      className={`mr-2 ${
                        output.status === "success"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {output.status === "success" ? "✅" : "❌"}
                    </span>
                    <strong>Status:</strong>{" "}
                    <span
                      className={
                        output.status === "success"
                          ? "text-green-700 dark:text-green-300"
                          : "text-red-700 dark:text-red-300"
                      }
                    >
                      {output.status || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-600 dark:text-gray-400 mr-2">
                      ⏱️
                    </span>
                    <strong>Time:</strong> <span>{output.time || "N/A"}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                {output === "Compiling"
                  ? "⏳ Compiling..."
                  : "You must run your code first"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InputOutput;
